import styled from "styled-components";
import {
  border,
  brand,
  radius,
  spacing,
  surface,
  text,
  typography,
} from "../../theme/tokens";

/**
 * Mobile Home dashboard styled components (Mobile DMS v1.1).
 *
 * Layout follows the mobile conventions of consumer/legal DMS apps
 * (Drive/Dropbox/Box/Clio): personal greeting, search front-and-center,
 * a thumb-sized "my work" card grid, then card lists — never tables.
 */

export const MobileHomeWrapper = styled.div`
  min-height: 100%;
  background: ${surface.page};
  padding: ${spacing[4]} ${spacing[4]} ${spacing[6]};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${spacing[5]};
`;

// ---------------------------------------------------------------------------
// Greeting
// ---------------------------------------------------------------------------

export const GreetingBlock = styled.header`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const GreetingDate = styled.div`
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.semibold};
  color: ${brand.primary};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

export const GreetingTitle = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: ${typography.weight.bold};
  color: ${text.primary};
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

// ---------------------------------------------------------------------------
// Search pill
// ---------------------------------------------------------------------------

export const SearchPill = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing[2.5]};
  width: 100%;
  padding: ${spacing[3]} ${spacing[4]};
  border: 1px solid ${border.default};
  border-radius: ${radius.pill};
  background: ${surface.surface};
  color: ${text.muted};
  font-size: ${typography.size.md};
  font-family: inherit;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(27, 32, 40, 0.05);
  -webkit-tap-highlight-color: transparent;

  &:focus-visible {
    outline: 2px solid ${brand.primary};
    outline-offset: 2px;
  }
`;

// ---------------------------------------------------------------------------
// "My work" stat card grid
// ---------------------------------------------------------------------------

export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing[3]};
`;

export const StatCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing[2]};
  padding: ${spacing[3.5]} ${spacing[4]};
  border: 1px solid ${border.light};
  border-radius: ${radius.xl};
  background: ${surface.surface};
  cursor: pointer;
  text-align: left;
  box-shadow: 0 1px 2px rgba(27, 32, 40, 0.05);
  -webkit-tap-highlight-color: transparent;
  min-height: 96px;

  &:active {
    background: ${surface.hover};
  }

  &:focus-visible {
    outline: 2px solid ${brand.primary};
    outline-offset: 2px;
  }
`;

export const StatIconChip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${radius.lg};
  background: ${({ $bg }) => $bg || brand.primaryLight};
  color: ${({ $fg }) => $fg || brand.primary};
`;

export const StatValueRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${spacing[1.5]};
  width: 100%;
`;

export const StatValue = styled.span`
  font-size: 22px;
  font-weight: ${typography.weight.bold};
  color: ${text.primary};
  font-variant-numeric: tabular-nums;
  line-height: 1;
`;

export const StatLabel = styled.span`
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.medium};
  color: ${text.tertiary};
  line-height: 1.3;
`;

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2.5]};
`;

export const SectionHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[2]};
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-size: ${typography.size.lg};
  font-weight: ${typography.weight.bold};
  color: ${text.primary};
  letter-spacing: -0.01em;
`;

export const SectionSubtitle = styled.p`
  margin: -${spacing[1]} 0 0;
  font-size: ${typography.size.sm};
  color: ${text.muted};
`;

export const SectionLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.semibold};
  color: ${brand.primary};
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
`;

export const SectionCard = styled.div`
  background: ${surface.surface};
  border: 1px solid ${border.light};
  border-radius: ${radius.xl};
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(27, 32, 40, 0.05);
`;

export const SectionEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing[1.5]};
  padding: ${spacing[6]} ${spacing[4]};
  color: ${text.muted};
  font-size: ${typography.size.sm};
  text-align: center;
`;

// ---------------------------------------------------------------------------
// Review cards
// ---------------------------------------------------------------------------

export const ReviewRow = styled.button`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  width: 100%;
  padding: ${spacing[3]} ${spacing[4]};
  border: none;
  border-bottom: 1px solid ${border.light};
  background: transparent;
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:last-child {
    border-bottom: none;
  }

  &:active {
    background: ${surface.hover};
  }
`;

/** Litify task subjects can run to paragraphs — clamp to two lines so
 * one row can't become a wall (full text available on the item). */
export const ReviewRowTitle = styled.span`
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};
  word-break: break-word;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
`;

export const ReviewRowMeta = styled.span`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  font-size: ${typography.size.sm};
  color: ${text.tertiary};
`;

// ---------------------------------------------------------------------------
// Activity feed
// ---------------------------------------------------------------------------

export const ActivityRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[3]};
  padding: ${spacing[3]} ${spacing[4]};
  border-bottom: 1px solid ${border.light};

  &:last-child {
    border-bottom: none;
  }
`;

export const ActivityDot = styled.span`
  width: 8px;
  height: 8px;
  margin-top: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $unread }) => ($unread ? brand.primary : border.default)};
`;

export const ActivityBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const ActivityMessage = styled.span`
  font-size: ${typography.size.sm};
  color: ${text.secondary};
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const ActivityTime = styled.span`
  font-size: ${typography.size.xs};
  color: ${text.muted};
  font-variant-numeric: tabular-nums;
`;
